import { useEffect, useState } from "react";
import { Page, Card, Field, Input, Select, Button, Money, Badge, Table, Empty } from "../../ui";
import { get, send } from "../../lib/api";
import { t } from "../../i18n";

const BU = "NUMEN";
type Tab = "gst" | "return" | "provinces";

export function TaxPage() {
  const [tab, setTab] = useState<Tab>("gst");
  return (
    <Page title={t("tax.title")} actions={
      <div className="tabs">
        <button className={tab === "gst" ? "tab active" : "tab"} onClick={() => setTab("gst")}>{t("tax.tab.gst")}</button>
        <button className={tab === "return" ? "tab active" : "tab"} onClick={() => setTab("return")}>{t("tax.tab.return")}</button>
        <button className={tab === "provinces" ? "tab active" : "tab"} onClick={() => setTab("provinces")}>{t("tax.tab.rates")}</button>
      </div>
    }>
      {tab === "gst" && <GstCalc />}
      {tab === "return" && <Gst34 />}
      {tab === "provinces" && <Provinces />}
    </Page>
  );
}

interface GstResult { rate: number; gst: number; tax_class: string; recoverable: boolean; account: string }
function GstCalc() {
  const [amount, setAmount] = useState("1000.00");
  const [province, setProvince] = useState("AB");
  const [txType, setTxType] = useState("SALE");
  const [supply, setSupply] = useState("DOMESTIC");
  const [res, setRes] = useState<GstResult | null>(null);
  const calc = async () => {
    setRes(await send<GstResult>("POST", "/api/v1/jurisdiction/CA/calc/gst", {
      amount: Number(amount), province, tx_type: txType, supply_type: supply, non_resident: supply === "EXPORT",
    }));
  };
  return (
    <div className="report-cols">
      <Card title={t("tax.calcTitle")}>
        <Field label={t("tax.amountBeforeTax")}><Input className="num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label={t("tax.province")}>
          <Select value={province} onChange={(e) => setProvince(e.target.value)}>{["AB","BC","MB","SK","QC","ON","NB","NL","NS","PE","NT","NU","YT"].map((p) => <option key={p}>{p}</option>)}</Select>
        </Field>
        <Field label={t("tax.transaction")}>
          <Select value={txType} onChange={(e) => setTxType(e.target.value)}><option value="SALE">{t("tax.txSale")}</option><option value="PURCHASE">{t("tax.txPurchase")}</option></Select>
        </Field>
        <Field label={t("tax.supply")}>
          <Select value={supply} onChange={(e) => setSupply(e.target.value)}><option value="DOMESTIC">{t("tax.domestic")}</option><option value="EXPORT">{t("tax.export")}</option></Select>
        </Field>
        <div className="form-actions"><Button onClick={calc}>{t("common.calculate")}</Button></div>
      </Card>
      {res && (
        <Card title={t("common.result")}>
          <div className="big-figure"><Money value={res.gst} /></div>
          <Table head={["", ""]}>
            <tr><td>{t("common.rate")}</td><td className="num">{(res.rate * 100).toFixed(2)}%</td></tr>
            <tr><td>{t("tax.taxClass")}</td><td className="num"><Badge tone={res.tax_class === "ZERO_RATED" ? "pending" : "ok"}>{res.tax_class}</Badge></td></tr>
            <tr><td>{t("tax.itc")}</td><td className="num">{res.recoverable ? t("tax.yes") : t("tax.no")}</td></tr>
            <tr><td>{t("common.account")}</td><td className="num">{res.account || "—"}</td></tr>
          </Table>
        </Card>
      )}
    </div>
  );
}

interface Gst34Doc { title: string; locale: string; net_tax: string; lines: { line: string; label: string; amount: string }[] }
function Gst34() {
  const [locale, setLocale] = useState("en");
  const [doc, setDoc] = useState<Gst34Doc | null>(null);
  useEffect(() => { get<Gst34Doc>(`/api/v1/ca/statutory/gst34?bu=${BU}&fy=2026&period=12&locale=${locale}`).then(setDoc).catch(() => setDoc(null)); }, [locale]);
  return (
    <Card title={doc?.title || t("tax.returnTitle")}>
      <div className="form-row">
        <Field label={t("common.language")}><Select value={locale} onChange={(e) => setLocale(e.target.value)}><option value="en">{t("tax.english")}</option><option value="fr">{t("tax.french")}</option></Select></Field>
      </div>
      {doc ? (
        <table className="data statement"><tbody>
          {doc.lines.map((l) => <tr key={l.line}><td>{t("tax.line", { n: l.line, label: l.label })}</td><td className="num"><Money value={l.amount} /></td></tr>)}
          <tr className="totals"><td>{t("tax.netTax")}</td><td className="num"><Money value={doc.net_tax} /></td></tr>
        </tbody></table>
      ) : <Empty>{t("common.noData")}</Empty>}
    </Card>
  );
}

interface Prov { province_cd: string; province_name: string; gst_hst_rate: string; pst_rate: string; total_rate: string; is_hst: boolean }
function Provinces() {
  const [rows, setRows] = useState<Prov[]>([]);
  useEffect(() => { get<{ provinces: Prov[] }>("/api/v1/ca/provinces").then((d) => setRows(d.provinces || [])).catch(() => setRows([])); }, []);
  return (
    <Card title={t("tax.ratesTitle")}>
      <Table head={[t("tax.province"), t("tax.col.gstHst"), t("tax.col.pst"), t("common.total"), t("tax.col.type")]}>
        {rows.map((p) => (
          <tr key={p.province_cd}>
            <td>{p.province_name}</td>
            <td className="num">{(Number(p.gst_hst_rate) * 100).toFixed(2)}%</td>
            <td className="num">{(Number(p.pst_rate) * 100).toFixed(2)}%</td>
            <td className="num">{(Number(p.total_rate) * 100).toFixed(2)}%</td>
            <td>{p.is_hst ? "HST" : "GST" + (Number(p.pst_rate) > 0 ? " + PST" : "")}</td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
